import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import { TransactionListRow } from "../../types/sale.type";
import {
  getQrPaymentByBill,
  updateTransactionPayment,
  verifyQrPayment,
} from "../../services";
import {
  QrPayment,
  getQrStatusLabel,
  holdsConfirmedMoney,
  isCheckable,
  isSettled,
} from "../../types/qr-payment.type";
import UnclaimedQrPicker from "./UnclaimedQrPicker";
import {
  PAYMENT_CHANNEL_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  getPaymentChannelLabel,
  getStatusLabel,
} from "./data";
import { formatNumber } from "../../utils";

type PaymentStatus = "paid" | "pending";

/**
 * Channels on which a collected amount may be stated, mirroring the API.
 *
 * Cash and transfer are the two a person hands over or types an amount into, so they are
 * the two that can arrive short. A QR moves exactly what the code was raised for and the
 * bank confirms it; credit is not a collection at all.
 */
const COLLECTABLE_CHANNELS = ["CASH", "BANK_TRANSFER"];

/**
 * The widest gap the API will accept between the bill and what arrived — under one baht.
 * Checked here as well so the difference can be shown to be wrong while it is being typed,
 * rather than as a refusal after pressing บันทึก.
 */
const MAX_ROUNDING_BAHT = 1;

interface PaymentEditDialogProps {
  open: boolean;
  bill: TransactionListRow | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Correct a bill's payment channel, settlement status and/or the money that actually landed.
 *
 * The case: the salesperson tapped "scan QR", the customer paid cash instead, and the
 * bill still says QR — so the remit total never reconciles.
 *
 * The same sale has a second ending. The customer changes their mind again, transfers
 * instead, and sends the round baht without the satang. Then the channel is not what is
 * wrong — the amount is — and until someone can record what arrived, the remit is short by
 * the difference with nothing to point at. ยอดรับจริง is that record; the shortfall is
 * written off as a discount in the accounting system, not here.
 *
 * Saving does **not** overwrite the bill: the API appends a new version, and the
 * previous values stay readable in the bill's version history. The copy says so,
 * because "แก้ไข" on a financial document otherwise implies the old value is gone.
 *
 * Only the changed fields are sent. Submitting with nothing changed is prevented by
 * disabling the button rather than by letting the API reject it — a 400 for "you
 * changed nothing" is a worse experience than a button that will not arm.
 *
 * The edit window (same business day; locked once the back office ticks ตรวจแล้ว) is
 * enforced by the API, not here. A client-side clock check would disagree with the
 * server around midnight Bangkok and would not know about the review at all. Refusals
 * surface through the shared axios interceptor.
 *
 * QR adds three rules the other channels do not have, because a QR payment is confirmed
 * by the bank against a reference rather than by anyone's memory of the sale:
 *
 * - **Off a confirmed QR is refused.** The money is in the account against that
 *   reference; relabelling the bill cash would leave the deposit short by its value with
 *   nothing to point at. Disabled here as well as refused by the API, so the reason can
 *   be read before the attempt rather than after it.
 * - **Onto QR must name the payment.** Back office cannot raise a QR after the fact, so
 *   the correction only means anything as "*this* existing payment settled it".
 * - **A QR still waiting can be chased from here.** The check writes the answer back and
 *   settles the bill with it, so the ordinary "customer transferred later" case needs no
 *   correction at all — which is why the button sits above the fields rather than in
 *   them.
 */
export default function PaymentEditDialog({
  open,
  bill,
  onClose,
  onSaved,
}: PaymentEditDialogProps) {
  const [paymentType, setPaymentType] = useState<string>("");
  const [paymentStatus, setPaymentStatus] = useState<string>("");
  const [saving, setSaving] = useState(false);
  // The QR behind this bill, if one settled it. `undefined` = not looked up yet.
  const [linkedQr, setLinkedQr] = useState<QrPayment | null | undefined>(
    undefined,
  );
  const [checking, setChecking] = useState(false);
  const [pickedQrUuid, setPickedQrUuid] = useState<string>("");
  /**
   * Kept as the raw string so the field can be EMPTY, which is its resting state and means
   * "do not touch what the bill already records".
   *
   * It deliberately does not open pre-filled with what was collected before: `received_amount`
   * is not carried on the transaction list (it lives on invoices / cash_sales, and
   * `v_transactions` does not select it — a view this repo has decided not to rewrite for one
   * column). Rather than show a figure that might be stale, the field starts blank and an
   * untouched field sends nothing, so a previously recorded collection cannot be silently
   * overwritten by a dialog that never knew about it.
   */
  const [receivedAmount, setReceivedAmount] = useState<string>("");

  useEffect(() => {
    if (!bill) return;
    setPaymentType(bill.payment_type ?? "");
    setPickedQrUuid("");
    setReceivedAmount("");
    // Only the two settlement states are selectable; a bill sitting in any other status
    // opens with no selection rather than a fabricated one.
    setPaymentStatus(
      bill.status === "paid" || bill.status === "pending" ? bill.status : "",
    );
  }, [bill]);

  /**
   * Looked up for EVERY bill, not only the ones still recorded as QR.
   *
   * It used to skip anything whose `payment_type` was not QR_CODE, on the reasoning that
   * only a QR bill can have a QR. That is not true of the bill this dialog most needs to
   * fix: a sale where the salesperson raised a QR and then switched to โอน keeps its QR
   * row, pointing at this bill, closed — and if the customer had already scanned, that row
   * is holding their money. Gating the lookup on the channel meant the one bill whose QR
   * mattered was the one bill that never fetched it, so the payment could not be seen, let
   * alone re-attached.
   */
  useEffect(() => {
    if (!open || !bill) return;
    let cancelled = false;
    setLinkedQr(undefined);
    getQrPaymentByBill(bill.uuid)
      // `{ payment, counter_closed }` since the backend started answering the counter
      // question itself. Only `payment` is read here — this dialog corrects a bill that has
      // already been collected, so whether the counter is shut is not its question.
      .then(({ data: { data } }) => {
        if (!cancelled) setLinkedQr((data?.payment as QrPayment) ?? null);
      })
      .catch(() => {
        // Rendered inline as "ดูข้อมูล QR ไม่ได้" rather than blocking the whole
        // dialog: correcting the channel does not depend on this lookup.
        if (!cancelled) setLinkedQr(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, bill]);

  if (!bill) return null;

  const originalType = bill.payment_type ?? "";
  const originalStatus =
    bill.status === "paid" || bill.status === "pending" ? bill.status : "";

  const typeChanged = paymentType !== "" && paymentType !== originalType;
  const statusChanged = paymentStatus !== "" && paymentStatus !== originalStatus;

  // The bank has confirmed money against this reference, so the channel is settled fact.
  const channelLocked = Boolean(linkedQr && isSettled(linkedQr.status));
  // Attaching a payment to a bill that is not already QR. Staying on QR is not a channel
  // change and needs nothing named.
  const needsQrPick = paymentType === "QR_CODE" && originalType !== "QR_CODE";
  const canCheckBank = Boolean(linkedQr && isCheckable(linkedQr.status));
  /**
   * This bill's own QR, once the bank's figures say it holds money.
   *
   * The candidate for putting a wrongly-switched sale back onto QR — and the reason the
   * check button above matters, because until someone presses it this is null and the row
   * looks like an ordinary abandoned code.
   */
  const billOwnQr =
    linkedQr && holdsConfirmedMoney(linkedQr) && !isSettled(linkedQr.status)
      ? linkedQr
      : null;

  /**
   * Whether a collected amount may be stated at all.
   *
   * Read off the channel the bill will END on, not the one it started on, so choosing QR in
   * this dialog takes the field away in the same breath. A QR the bank has confirmed also
   * closes it: the bank's figure is the authority on what that bill collected, and the API
   * refuses to have it contradicted.
   */
  const collectableChannel = COLLECTABLE_CHANNELS.includes(paymentType);
  const canStateCollected = collectableChannel && !channelLocked;

  /**
   * A channel change re-settles the bill, so the API recomputes the collected amount from
   * the channel it lands on and drops anything sent alongside it — a figure typed against
   * the old channel describes money collected a different way.
   *
   * The field is therefore disabled rather than merely ignored. Leaving it live would take
   * a number, show a difference, and then save something else: the one outcome worse than
   * not offering the field at all.
   */
  const collectedIsSystemDerived = typeChanged;

  const billTotal = Number(bill.grand_total);
  const collected = receivedAmount === "" ? null : Number(receivedAmount);
  const collectedIsNumber = collected !== null && Number.isFinite(collected);
  // The shortfall the accounting system writes off as a discount. Only ever shown for a
  // figure that would actually be accepted.
  const shortfall = collectedIsNumber ? billTotal - (collected as number) : 0;
  const collectedOutOfRange =
    !collectedIsSystemDerived &&
    collectedIsNumber &&
    (shortfall < 0 || shortfall >= MAX_ROUNDING_BAHT);
  const collectedChanged =
    !collectedIsSystemDerived && collectedIsNumber && !collectedOutOfRange;

  const hasChanges =
    (typeChanged || statusChanged || collectedChanged) &&
    (!needsQrPick || pickedQrUuid !== "") &&
    !collectedOutOfRange;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateTransactionPayment(bill.uuid, {
        ...(typeChanged && { payment_type: paymentType }),
        ...(statusChanged && { payment_status: paymentStatus as PaymentStatus }),
        ...(needsQrPick && { qr_payment_uuid: pickedQrUuid }),
        ...(collectedChanged && { received_amount: collected as number }),
      });
      onSaved();
    } catch {
      // Already surfaced by the axios interceptor — keep the dialog open so the user
      // can see what they had entered and retry or cancel.
    } finally {
      setSaving(false);
    }
  };

  /**
   * Ask the bank about this QR again.
   *
   * On a hit the API settles the bill in the same call, so this closes the ordinary
   * "customer said they would transfer later" case without any correction at all. The
   * dialog closes through `onSaved` so the list reflects the new status.
   */
  const handleCheckBank = async () => {
    if (!linkedQr) return;
    setChecking(true);
    try {
      const {
        data: { data },
      } = await verifyQrPayment(linkedQr.uuid);
      const updated = data as QrPayment;
      setLinkedQr(updated);
      if (isSettled(updated.status)) onSaved();
    } catch {
      // Surfaced by the axios interceptor; the dialog stays open so the user can retry.
    } finally {
      setChecking(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>แก้ไขข้อมูลการชำระเงิน</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            เลขที่บิล
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {bill.transaction_no}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ปัจจุบัน: {getPaymentChannelLabel(bill.payment_type)} ·{" "}
            {getStatusLabel(bill.status)}
          </Typography>
        </Box>

        {linkedQr && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: (theme) => theme.palette.action.hover,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography variant="body2" fontWeight={600}>
                QR ของบิลนี้
              </Typography>
              <Chip
                size="small"
                label={getQrStatusLabel(linkedQr.status)}
                color={isSettled(linkedQr.status) ? "success" : "warning"}
                variant="outlined"
              />
            </Box>
            {isSettled(linkedQr.status) ? (
              <Typography variant="caption" color="text.secondary">
                ธนาคารยืนยันแล้ว เปลี่ยนช่องทางไม่ได้
              </Typography>
            ) : (
              <>
                <Typography
                  variant="caption"
                  color={billOwnQr ? "error.main" : "text.secondary"}
                  sx={{ display: "block", mb: 1 }}
                >
                  {/* Three different situations, and telling them apart is the whole
                      value of this line. A cancelled QR the bank says was paid is money
                      sitting against a sale that disowned it — the operator has to know
                      that is what they are looking at, and what to do about it. */}
                  {(() => {
                    if (billOwnQr) {
                      return "ธนาคารยืนยันว่ามีเงินเข้า QR ใบนี้ แต่บิลถูกเปลี่ยนไปช่องทางอื่น เลือกช่องทางเป็น QR Code แล้วผูกรายการนี้กลับ";
                    }
                    if (linkedQr.status === "CANCELLED") {
                      return "QR ใบนี้ถูกปิดตอนเปลี่ยนช่องทาง ถ้าลูกค้าสแกนจ่ายไปแล้ว ให้กดตรวจสอบกับธนาคาร";
                    }
                    return "ยอดนี้ยังไม่ถูกนับในเงินโอน จนกว่าเงินจะเข้า";
                  })()}
                </Typography>
                {canCheckBank && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCheckBank}
                    disabled={checking || saving}
                    startIcon={
                      checking ? <CircularProgress size={14} /> : undefined
                    }
                  >
                    ตรวจสอบกับธนาคาร
                  </Button>
                )}
              </>
            )}
          </Box>
        )}

        <TextField
          select
          fullWidth
          size="small"
          label="ช่องทางการจ่ายเงิน"
          value={paymentType}
          disabled={saving || channelLocked}
          onChange={(e) => setPaymentType(e.target.value)}
          sx={{ mb: 2 }}
        >
          {PAYMENT_CHANNEL_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {needsQrPick && (
          <UnclaimedQrPicker
            billTotal={Number(bill.grand_total)}
            selectedUuid={pickedQrUuid}
            onSelect={setPickedQrUuid}
            disabled={saving}
            billQr={billOwnQr}
          />
        )}

        <TextField
          select
          fullWidth
          size="small"
          label="สถานะการจ่ายเงิน"
          value={paymentStatus}
          disabled={saving}
          onChange={(e) => setPaymentStatus(e.target.value)}
          sx={{ mb: 2 }}
        >
          {PAYMENT_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {canStateCollected && (
          <>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="ยอดรับจริง (บาท)"
              value={collectedIsSystemDerived ? "" : receivedAmount}
              disabled={saving || collectedIsSystemDerived}
              onChange={(e) => setReceivedAmount(e.target.value)}
              error={collectedOutOfRange}
              helperText={
                collectedIsSystemDerived
                  ? "ระบบจะคำนวณยอดรับจริงใหม่ตามช่องทางที่เลือก กรอกเองไม่ได้"
                  : collectedOutOfRange
                    ? `กรอกได้ไม่เกินยอดบิล ${formatNumber(billTotal)} และต่ำกว่าได้ไม่ถึง ${MAX_ROUNDING_BAHT} บาท`
                    : `ยอดบิล ${formatNumber(billTotal)} — เว้นว่างถ้าได้รับเต็มจำนวน`
              }
              slotProps={{ htmlInput: { step: "0.01", min: 0 } }}
              sx={{ mb: 1 }}
            />
            {/* Says what the system will do with it, not just that the field is shut. The
                two channels round differently — เงินสด keeps whole baht, โอนเงิน moves the
                exact amount — so which one was picked changes the answer. */}
            {collectedIsSystemDerived && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 2 }}
              >
                {paymentType === "CASH"
                  ? `เปลี่ยนเป็นเงินสด ระบบจะตัดเศษสตางค์ให้เป็น ${formatNumber(Math.floor(billTotal))} บาท`
                  : "เปลี่ยนเป็นโอนเงิน ระบบจะบันทึกยอดเต็มตามบิล"}
              </Typography>
            )}
            {/* The number the branch is actually after: what to write off. Shown only once
                the figure is one the API would accept, so a half-typed amount does not
                flash an alarming difference. */}
            {collectedChanged && shortfall > 0 && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ display: "block", mb: 2 }}
              >
                ส่วนต่าง {formatNumber(shortfall)} บาท — ตัดเป็นส่วนลดในระบบบัญชี
              </Typography>
            )}
            {collectedChanged && shortfall === 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 2 }}
              >
                ได้รับเต็มจำนวน ไม่มีส่วนต่าง
              </Typography>
            )}
          </>
        )}

        {/* Said only where the field is missing BECAUSE of the channel — not on a bill the
            bank has already settled, which has its own explanation above. */}
        {!collectableChannel && !channelLocked && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 2 }}
          >
            กรอกยอดรับจริงได้เฉพาะบิลเงินสดและโอนเงิน
          </Typography>
        )}

        <Alert severity="info" sx={{ mt: 1 }}>
          การแก้ไขจะบันทึกเป็นเวอร์ชันใหม่ ข้อมูลเดิมยังดูย้อนหลังได้
          {needsQrPick
            ? " การผูก QR ทำได้แม้เป็นบิลย้อนหลัง จนกว่าฝ่ายบัญชีจะกดตรวจแล้ว"
            : " และแก้ไขได้เฉพาะวันที่ขายเท่านั้น จนกว่าฝ่ายบัญชีจะกดตรวจแล้ว"}
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
